import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './auth.css';

type VerifyState = 'verifying' | 'success' | 'error';

export function MagicLinkVerifyPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<VerifyState>('verifying');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { verifyMagicLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setState('error');
        setErrorMessage('Invalid magic link. No token provided.');
        return;
      }

      try {
        await verifyMagicLink(token);
        setState('success');
        // Redirect after a brief delay to show success state
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      } catch (err) {
        setState('error');
        setErrorMessage(err instanceof Error ? err.message : 'Failed to verify magic link');
      }
    };

    verify();
  }, [token, verifyMagicLink, navigate]);

  // If already authenticated, redirect
  useEffect(() => {
    if (isAuthenticated && state === 'success') {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, state, navigate]);

  return (
    <div className="auth-container">
      <div className="auth-card">
        {state === 'verifying' && (
          <div className="magic-link-verifying">
            <div className="loading-spinner"></div>
            <h1>Signing you in...</h1>
            <p className="auth-subtitle">Please wait while we verify your magic link.</p>
          </div>
        )}

        {state === 'success' && (
          <div className="magic-link-success">
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h1>You're signed in!</h1>
            <p className="auth-subtitle">Redirecting you to the dashboard...</p>
          </div>
        )}

        {state === 'error' && (
          <div className="magic-link-error">
            <div className="error-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h1>Unable to sign in</h1>
            <p className="auth-subtitle">{errorMessage}</p>
            <p className="magic-link-hint">
              Magic links expire after 15 minutes and can only be used once.
            </p>
            <Link to="/login" className="auth-button">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
